<script setup>
import { ref } from 'vue'

defineProps({
    options: {
        type: Array,
        default: () => [],
    },
    prefix: {
        type: String,
        default: '',
    },
    active: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['change'])

const open = ref(false)

function setActive(option) {
    emit('change', option)
    open.value = false
}
</script>

<template>
    <div class="base-custom-select">
        <div
            class="toggle"
            @click="open = !open"
        >
            <div class="toggle-main">
                <div
                    v-if="prefix"
                    class="prefix"
                >{{ prefix }}</div>
                <div class="title">{{ active }}</div>
            </div>
            <div class="arrow">
                <BaseIcon
                    v-if="open"
                    class="icon"
                    name="expand_less"
                />
                <BaseIcon
                    v-else
                    class="icon"
                    name="expand_more"
                />
            </div>
        </div>
        <div
            v-show="open"
            class="options"
        >
            <div
                v-for="option in options"
                :key="option"
                class="option"
                @click="setActive(option)"
            >
                {{ option }}
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';

@import '@design';

.base-custom-select {
    position: relative;

    @extend %contentSubtitleM;

    z-index: $layer-dropdown-z-index;
    background-color: $c-black;
}

.toggle {
    display: flex;
    align-items: stretch;
    height: math.div($s-h, 2);
    cursor: pointer;
    border: 1px solid $c-shark;
}

.toggle-main {
    display: flex;
    align-items: center;
    padding: $s-s;
}

.prefix {
    padding-right: $s-xs;
    font-weight: 700;
    white-space: nowrap;
}

// Never wraps: a select's value belongs on one line, and the longest of them is what the
// control should be sized to. 12px, matching the container, so a long value ("HI-FI · 320
// kbps") fits beside its label rather than folding under it.
.title {
    font-size: 12px;
    color: $c-white;
    white-space: nowrap;
}

.arrow {
    display: flex;
    align-items: center;
    padding: 0 $s-xs;
    border-left: 1px solid $c-shark;
}

.icon {
    @include square($s-l);

    fill: $c-highlight;
}

.options {
    position: absolute;
    bottom: 1px;
    width: 100%;
    padding: $s-xxs;
    font-size: 12px;
    color: $c-white;
    background-color: $c-black;
    border: 1px solid $c-shark;
    transform: translateY(100%);
}

// The list had no hover state at all — nothing said which option the pointer was on. The
// padding is what gives the highlight a row to fill; $c-hover-bg is the token that exists
// for exactly this.
.option {
    padding: $s-xs $s-s;
    white-space: nowrap;
    cursor: pointer;
    border-radius: $button-border-radius;

    &:hover {
        background: $c-hover-bg;
    }
}
</style>
